import { ImageType } from "@/types/baseTypes";
import { PostsGridElementType } from "../components/PostCard/PostCard";
import { WPClient } from "../wp/client/WPClient";
import { PostsGridModuleType } from "../modules/PostGrid/PostGrid";
import { getDataArrayGeneric } from "./getData";

/* jscpd:ignore-start */
interface FetchPostsProps {
  page: number;
  postsPerPage: number;
  offset: number;
  locale: string | undefined;
  defaultLocale: string | undefined;
  module: PostsGridModuleType;
  fallback_options: "header-img" | "teaser-text";
  fallback_image?: ImageType | false;
  abortController?: AbortController;
  isNobleElements?: boolean;
}
/* jscpd:ignore-end */

export const fetchPosts = async ({
  module,
  page,
  postsPerPage,
  offset,
  locale,
  defaultLocale,
  abortController,
  isNobleElements,
}: FetchPostsProps) => {
  const client = new WPClient({ locale, defaultLocale });

  const { type, posts: _posts, press, categories } = module.grid;

  const selectedPosts = type === "posts" ? _posts : press;
  let selectedCategories: number[] = [];
  if (type === "categories") selectedCategories = categories || [];

  const postsResult =
    type === "posts" || type === "categories"
      ? await client.getPosts({
          type: "blog",
          page: page,
          perPage: postsPerPage,
          offset,
          fields: [
            "id",
            "date",
            "date_gmt",
            "modified_gmt",
            "_embedded",
            "acf.header_post",
            "link",
            "categories",
            "type",
            "location_category",
          ],
          ...(type === "posts" && {
            includeIds: { ids: selectedPosts || [], keepOrder: true },
          }),
          ...(type === "categories" &&
            selectedCategories.length > 0 && {
              includeCategories: [
                { category: "categories", ids: selectedCategories },
              ],
            }),
          includeTaxonomies: [
            { taxonomy: "location_category", ids: [isNobleElements ? 41 : 40] },
          ],
          abortController,
        })
      : await client.getPosts({
          type: "press",
          fetchAll: true,
          fields: [
            "id",
            "date",
            "date_gmt",
            "modified_gmt",
            "_embedded",
            "acf.header_press",
            "link",
            "press_category",
            "location",
            "type",
          ],
          abortController,
          includeTaxonomies: [
            { taxonomy: "location_category", ids: [isNobleElements ? 41 : 40] },
          ],
        });

  // Collect unique category IDs across all posts
  const allCategoryIds = [
    ...new Set(
      postsResult.posts.flatMap(
        (post) =>
          post._embedded?.["wp:term"]?.[0]?.map(
            (t) => (t as unknown as { id: number }).id,
          ) ?? [],
      ),
    ),
  ];

  // Build a map of categoryId -> frontend page link via overview_page_id
  const categoryLinkMap = new Map<number, string>();
  if (allCategoryIds.length > 0) {
    const { pageData: categoriesMeta } = await getDataArrayGeneric<
      Array<{ id: number; meta: { overview_page_id: number } }>
    >(
      `/categories?include=${allCategoryIds.join(",")}&_fields=id,meta`,
      locale,
      defaultLocale,
    );

    const pageIds = [
      ...new Set(
        categoriesMeta.map((c) => c.meta?.overview_page_id).filter(Boolean),
      ),
    ];

    if (pageIds.length > 0) {
      const { pageData: pages } = await getDataArrayGeneric<
        Array<{ id: number; link: string }>
      >(
        `/pages?include=${pageIds.join(",")}&_fields=id,link`,
        locale,
        defaultLocale,
      );

      const pageIdToLink = new Map(pages.map((p) => [p.id, p.link]));
      for (const cat of categoriesMeta) {
        const pageLink = pageIdToLink.get(cat.meta?.overview_page_id);
        if (pageLink) categoryLinkMap.set(cat.id, pageLink);
      }
    }
  }

  const posts = postsResult.posts.map((post) => {
    const isBlog = post.type === "blog";
    const selectedHeader = isBlog
      ? post.acf?.header_post
      : post.acf?.header_press;

    const _categories = (post._embedded?.["wp:term"]?.[0] ?? []).map((cat) => ({
      ...cat,
      ...(categoryLinkMap.has((cat as unknown as { id: number }).id) && {
        link: categoryLinkMap.get((cat as unknown as { id: number }).id)!,
      }),
    }));

    const _location = post._embedded?.["wp:term"]?.[1]
      ? post._embedded["wp:term"][1].map((t) => t.slug)
      : [];

    const image =
      (isBlog && post.acf?.header_post?.media?.image_desktop) ??
      selectedHeader.image;

    return {
      id: post.id,
      date: post.date || post.date_gmt || post.modified_gmt,
      title: selectedHeader.title,
      image: image,
      excerpt:
        "excerpt" in selectedHeader
          ? selectedHeader.excerpt
          : selectedHeader.content,
      link: post.link,
      categories: _categories,
      location: _location,
    } as PostsGridElementType;
  });

  return {
    posts,
    totalPosts: postsResult.totalPosts,
  };
};
